import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { deleteGiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.controller';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { GiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Link } from 'react-router-dom';

// 배송형 여부 — 배송 상세나 감사인쇄가 설정된 브랜드
const isDeliveryBrand = (brand: GiftItemBrand) => {
  const d = brand.deliveryDetail;
  return Boolean(
    brand.thanksPrint?.enabled ||
      (d &&
        (d.shippingInfo ||
          d.shippingFeeInfo ||
          d.shippingFee != null ||
          d.freeShippingMinQuantity != null ||
          (d.notifyPhoneNumbers && d.notifyPhoneNumbers.length > 0))),
  );
};

// 쿠폰형 여부 — 쿠폰 상세가 설정된 브랜드
const isCouponBrand = (brand: GiftItemBrand) =>
  Boolean(brand.couponDetail?.validPeriod || brand.couponDetail?.placeOfExchange);

const accountEntityLabel = (entity?: AccountEntity) => {
  switch (entity) {
    case AccountEntity.Mari:
      return '마리';
    case AccountEntity.Gaon:
      return '가온';
    default:
      return '미정';
  }
};

const accountEntityBadgeClass = (entity?: AccountEntity) => {
  switch (entity) {
    case AccountEntity.Mari:
      return 'bg-blue-100 text-blue-700';
    case AccountEntity.Gaon:
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-gray-100 text-gray-500';
  }
};

export const GiftItemBrandListPage: FC = function GiftItemBrandListPage() {
  const { sellerRole, isAdmin } = useAuth();

  useGiftItemBrandListHook(sellerRole());

  // 정산정보는 플랫폼↔공급사 정산용이라 관리자에게만 노출
  const showSettlement = isAdmin();

  const status = useRecoilValue(giftItemBrandListStatusAtom);
  const data = useRecoilValue(giftItemBrandListDataAtom);
  const setData = useSetRecoilState(giftItemBrandListDataAtom);

  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // 검색 필터 + 배송형 우선 정렬 (배송형 > 쿠폰형 > 그 외, 그룹 내 기존 순서 유지)
  const filteredData = useMemo(() => {
    if (!data) return [];

    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? data.filter((item) => item.name.toLowerCase().includes(query))
      : data;

    const sortGroup = (brand: GiftItemBrand) =>
      isDeliveryBrand(brand) ? 0 : isCouponBrand(brand) ? 1 : 2;
    return [...filtered].sort((a, b) => sortGroup(a) - sortGroup(b));
  }, [data, searchQuery]);

  // 삭제 핸들러
  const handleDelete = useCallback(
    async (brand: GiftItemBrand) => {
      if (!confirm(`"${brand.name}" 브랜드를 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(brand._id);
        await deleteGiftItemBrand(brand._id, sellerRole());
        // 목록에서 제거
        setData((prev) => prev.filter((item) => item._id !== brand._id));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery ? '검색 결과가 없습니다.' : '등록된 상품 브랜드가 없습니다.'}
          </div>
        );
      }

      return (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                <th className="whitespace-nowrap px-4 py-3 font-medium">브랜드명</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">유형</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">배송 추가금</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">배송 방식</th>
                <th className="whitespace-nowrap px-4 py-3 font-medium">쿠폰 정보</th>
                {showSettlement && (
                  <>
                    <th className="whitespace-nowrap px-4 py-3 font-medium">
                      정산 주체/일
                    </th>
                    <th className="whitespace-nowrap px-4 py-3 font-medium">
                      정산 단가
                    </th>
                  </>
                )}
                <th className="whitespace-nowrap px-4 py-3 font-medium">등록일</th>
                <th className="whitespace-nowrap px-4 py-3 text-right font-medium">
                  관리
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => {
                const delivery = isDeliveryBrand(item);
                const coupon = isCouponBrand(item);
                const d = item.deliveryDetail;
                const si = item.settlementInfo;
                const couponSummary = [
                  item.couponDetail?.validPeriod,
                  item.couponDetail?.placeOfExchange,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <tr key={item._id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="break-keep px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
                          <span className="text-sm font-bold">
                            {item.name.charAt(0)}
                          </span>
                        </div>
                        <span className="font-medium">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {delivery && (
                          <span className="whitespace-nowrap rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700">
                            배송형
                          </span>
                        )}
                        {coupon && (
                          <span className="whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                            쿠폰형
                          </span>
                        )}
                        {!delivery && !coupon && (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="break-keep px-4 py-3">
                      {delivery && d?.shippingFee != null ? (
                        <div className="text-xs text-gray-600">
                          <p>{d.shippingFee.toLocaleString()}원/개</p>
                          {d.freeShippingMinQuantity != null && (
                            <p className="text-gray-400">
                              {d.freeShippingMinQuantity}개↑ 면제
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="break-keep px-4 py-3">
                      {delivery ? (
                        <div className="text-xs text-gray-600">
                          <p>
                            {item.allowIndividualDelivery === false
                              ? '일괄배송 전용'
                              : '개별배송 허용'}
                            {item.thanksPrint?.enabled && (
                              <span className="ml-1 whitespace-nowrap rounded bg-rose-100 px-1 py-0.5 text-rose-600">
                                감사인쇄
                              </span>
                            )}
                          </p>
                          {d?.notifyPhoneNumbers && d.notifyPhoneNumbers.length > 0 && (
                            <p className="text-gray-400">
                              알림번호 {d.notifyPhoneNumbers.length}건
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {coupon ? (
                        <p
                          title={couponSummary}
                          className="max-w-[160px] truncate text-xs text-gray-500"
                        >
                          {couponSummary}
                        </p>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    {showSettlement && (
                      <>
                        <td className="break-keep px-4 py-3">
                          <div className="text-xs text-gray-600">
                            <span
                              className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 font-medium ${accountEntityBadgeClass(
                                si?.accountEntity,
                              )}`}
                            >
                              {accountEntityLabel(si?.accountEntity)}
                            </span>
                            {si?.settlementDay != null && (
                              <p className="mt-0.5 text-gray-400">
                                익월 {si.settlementDay}일
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="break-keep px-4 py-3">
                          {si?.shippingFee != null || si?.courierFee != null ? (
                            <div className="text-xs text-gray-600">
                              {si?.shippingFee != null && (
                                <p>
                                  추가 {si.shippingFee.toLocaleString()}원/개
                                  {si.freeShippingMinQuantity != null && (
                                    <span className="text-gray-400">
                                      {' '}
                                      ({si.freeShippingMinQuantity}개↑ 면제)
                                    </span>
                                  )}
                                </p>
                              )}
                              {si?.courierFee != null && (
                                <p>
                                  택배 {si.courierFee.toLocaleString()}원/건
                                  {si.courierFreeMinQuantity != null && (
                                    <span className="text-gray-400">
                                      {' '}
                                      ({si.courierFreeMinQuantity}개↑ 무료)
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      </>
                    )}
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString('ko-KR')
                        : '-'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/gift-seller/item-brand/${item._id}/edit`}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                        >
                          수정
                        </Link>
                        <button
                          onClick={() => handleDelete(item)}
                          disabled={deletingId === item._id}
                          className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingId === item._id ? '삭제 중...' : '삭제'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [status, filteredData, searchQuery, deletingId, handleDelete, showSettlement]);

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">상품 브랜드 관리</h1>
          <Link to="/gift-seller/item-brand/new">
            <Button className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">
              + 브랜드 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 text-sm text-gray-600">
            전체 <span className="font-medium text-gray-900">{data?.length || 0}</span>개
          </div>
        )}
      </div>

      {/* 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="브랜드명 검색..."
            className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        {/* 검색 결과 수 */}
        {status === 'success' && searchQuery && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredData.length}개 표시 (전체 {data?.length || 0}개 중)
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
